import React, { useCallback, useEffect, useState } from "react";
import {
  ActionIcon,
  Badge,
  Box,
  Button,
  Center,
  Collapse,
  Group,
  Loader,
  Paper,
  SegmentedControl,
  Select,
  SimpleGrid,
  Stack,
  Text,
  Tooltip,
  UnstyledButton,
} from "@mantine/core";
import {
  IconChevronDown,
  IconChevronRight,
  IconPlus,
  IconAlertTriangle,
} from "@tabler/icons-react";
import { useAuth } from "../../context/AuthContext";
import { getApiEndpoint } from "../../config/config";
import AddOperatingLevelEntityModal from "./AddOperatingLevelEntityModal";
import AddMarketingPartnerModal from "./AddMarketingPartnerModal";
import EditMarketingPartnerModal from "./EditMarketingPartnerModal";
import EditOfficeEntityModal from "./EditOfficeEntityModal";
import AddOfficeModal from "./AddOfficeModal";
import EditOperatingLevelEntityModal from "./EditOperatingLevelEntityModal";
import EditOperatingLevelTeamsModal from "./EditOperatingLevelTeamsModal";
import "./OrganizationLevelsTab.css";

function OperationLevelItem({
  entity,
  parentEntityId = null,
  onAdd,
  onEdit,
  onEditPartner,
  expandSignal,
  allOrgLevels = [],
  baseOrgLevelName = "",
  sellingYearId,
  onTeamsUpdated,
  locked = false,
}) {
  const isMarketingPartner = entity.is_marketing_partner === true;
  const children = Array.isArray(entity.operations) ? entity.operations : [];
  const isInactive = entity.is_active === false;
  const isBaseType = entity.operation_type?.base_operation_type === true;
  const assignedTeams = Array.isArray(entity.assigned_org_levels)
    ? entity.assigned_org_levels
    : [];
  const teamCount = assignedTeams.length;
  const orgLevelLabel = baseOrgLevelName || "Teams";
  const orgLevelNameById = new Map(allOrgLevels.map(o => [o.id, o.name]));
  const [teamsOpen, setTeamsOpen] = useState(false);
  const [editTeamsOpen, setEditTeamsOpen] = useState(false);
  const [childrenOpen, setChildrenOpen] = useState(false);
  const hasChildren = children.length > 0;

  useEffect(() => {
    if (expandSignal > 0) {
      setChildrenOpen(true);
      setTeamsOpen(true);
    }
  }, [expandSignal]);

  const renderTeamsSection = () => (
    <Box mt="xs">
      <Group justify="space-between" align="center">
        <UnstyledButton onClick={() => setTeamsOpen(o => !o)}>
          <Group gap="xs" align="center">
            {teamsOpen ? (
              <IconChevronDown size={14} />
            ) : (
              <IconChevronRight size={14} />
            )}
            <Text size="sm">
              {orgLevelLabel} Assignments: {teamCount}
            </Text>
          </Group>
        </UnstyledButton>
        {!isInactive && !locked && (
          <Button
            variant="subtle"
            size="xs"
            onClick={() => setEditTeamsOpen(true)}
          >
            Edit {orgLevelLabel} Assignments
          </Button>
        )}
      </Group>
      <Collapse in={teamsOpen}>
        <Stack gap="xs" mt="xs" pl="md">
          {assignedTeams.length === 0 ? (
            <Text size="sm" c="dimmed">
              No {orgLevelLabel.toLowerCase()} assignments
            </Text>
          ) : (
            assignedTeams.map((team, idx) => (
              <Text key={team.id ?? idx} size="sm">
                {orgLevelNameById.get(team.org_level_entity_id) ?? team.name}
              </Text>
            ))
          )}
        </Stack>
      </Collapse>
      <EditOperatingLevelTeamsModal
        isOpen={editTeamsOpen}
        onClose={() => setEditTeamsOpen(false)}
        entity={entity}
        allOrgLevels={allOrgLevels}
        baseOrgLevelName={baseOrgLevelName}
        sellingYearId={sellingYearId}
        onTeamsUpdated={onTeamsUpdated}
      />
    </Box>
  );

  return (
    <Box
      style={{
        padding: ".75rem",
        border: "1px solid #e0e0e0",
        borderRadius: "8px",
        backgroundColor: isInactive ? "#e0e0e0" : "#fafafa",
      }}
    >
      <Group justify="space-between" align="flex-start">
        <Stack gap={2}>
          <Group gap="xs" align="center">
            {hasChildren && (
              <ActionIcon
                variant="subtle"
                size="md"
                onClick={() => setChildrenOpen(o => !o)}
                aria-label={
                  childrenOpen ? "Collapse children" : "Expand children"
                }
              >
                {childrenOpen ? (
                  <IconChevronDown size={16} />
                ) : (
                  <IconChevronRight size={16} />
                )}
              </ActionIcon>
            )}
            <Text size="lg">
              {entity.operation_type && entity.operation_type.name && (
                <span style={{ fontStyle: "italic", fontSize: "0.85em" }}>
                  {entity.operation_type.name}:{" "}
                </span>
              )}
              {isInactive || locked ? (
                <span style={{ fontWeight: isBaseType ? 700 : 400 }}>
                  {entity.name}
                </span>
              ) : (
                <UnstyledButton
                  onClick={() =>
                    isMarketingPartner && onEditPartner
                      ? onEditPartner(entity)
                      : onEdit(entity, parentEntityId)
                  }
                  style={{
                    fontWeight: isBaseType ? 700 : 400,
                    fontSize: "inherit",
                    color: "inherit",
                  }}
                >
                  {entity.name}
                </UnstyledButton>
              )}
            </Text>
            {isInactive && (
              <Badge color="gray" variant="filled">
                Deactivated
              </Badge>
            )}
            {entity.history_id != null && (
              <Tooltip
                multiline
                withArrow
                label={
                  Array.isArray(entity.history) && entity.history.length > 0 ? (
                    <Stack gap={2}>
                      {entity.history.map((item, index) => (
                        <Text key={index} size="xs">
                          {item.name}
                          {item.operation_type_name
                            ? ` (${item.operation_type_name})`
                            : ""}{" "}
                          — {item.selling_year_name ?? "N/A"} · Revenue:{" "}
                          {item.revenue ?? "N/A"} · Accounts:{" "}
                          {item.number_of_accounts ?? "N/A"}
                        </Text>
                      ))}
                    </Stack>
                  ) : (
                    "No history"
                  )
                }
              >
                <Badge variant="light" style={{ cursor: "default" }}>
                  {entity.history_id}
                </Badge>
              </Tooltip>
            )}
            {(entity.is_marketing_partner || isBaseType) &&
              !entity.fieldroutes_connection_id && (
                <Tooltip label="Missing FieldRoutes connection" withArrow>
                  <span
                    style={{ display: "inline-flex", cursor: "default" }}
                    aria-label="Missing FieldRoutes connection"
                  >
                    <IconAlertTriangle
                      size={18}
                      color="var(--mantine-color-orange-6)"
                    />
                  </span>
                </Tooltip>
              )}
          </Group>
          <Text size="sm" c="dimmed">
            Revenue: {entity.revenue ?? "N/A"} · Accounts:{" "}
            {entity.number_of_accounts ?? "N/A"}
          </Text>
        </Stack>
        {!isBaseType && !isInactive && !locked && (
          <ActionIcon
            variant="subtle"
            size="md"
            onClick={() => onAdd(entity)}
            aria-label="Add child operation"
          >
            <IconPlus size={16} />
          </ActionIcon>
        )}
      </Group>

      {isBaseType && renderTeamsSection()}

      {hasChildren && (
        <Collapse in={childrenOpen}>
          <Stack gap="sm" mt="sm" pl="md">
            {children.map(child => (
              <OperationLevelItem
                key={child.id}
                entity={child}
                parentEntityId={entity.id}
                onAdd={onAdd}
                onEdit={onEdit}
                onEditPartner={onEditPartner}
                expandSignal={expandSignal}
                allOrgLevels={allOrgLevels}
                baseOrgLevelName={baseOrgLevelName}
                sellingYearId={sellingYearId}
                onTeamsUpdated={onTeamsUpdated}
                locked={locked}
              />
            ))}
          </Stack>
        </Collapse>
      )}
    </Box>
  );
}

function OperationChartNode({ entity }) {
  const children = Array.isArray(entity.operations) ? entity.operations : [];
  const typeName = entity.operation_type?.name;
  const isBaseType = entity.operation_type?.base_operation_type === true;
  const memberList = Array.isArray(entity.member_list)
    ? entity.member_list
    : [];
  const memberCount = entity.member_count ?? memberList.length;
  return (
    <li>
      <div className="org-chart-card">
        {typeName && <div className="org-chart-type">{typeName}</div>}
        <div className="org-chart-name">{entity.name}</div>
        <div className="org-chart-stats">
          Revenue: {entity.revenue ?? "N/A"} · Accounts:{" "}
          {entity.number_of_accounts ?? "N/A"}
        </div>
        {isBaseType && (
          <div className="org-chart-members">Members: {memberCount}</div>
        )}
      </div>
      {children.length > 0 && (
        <ul>
          {children.map(child => (
            <OperationChartNode key={child.id} entity={child} />
          ))}
        </ul>
      )}
    </li>
  );
}

function OperatingLevelsTab() {
  const { getVerifiedJWT } = useAuth();
  const [operations, setOperations] = useState([]);
  const [availableUsers, setAvailableUsers] = useState([]);
  const [entitiesByType, setEntitiesByType] = useState([]);
  const [entityList, setEntityList] = useState([]);
  const [operationTypes, setOperationTypes] = useState([]);
  const [marketingPartners, setMarketingPartners] = useState([]);
  const [orgLevelOptions, setOrgLevelOptions] = useState([]);
  const [baseOrgLevelName, setBaseOrgLevelName] = useState("");
  const [baseOperationLevelName, setBaseOperationLevelName] = useState("");
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addParent, setAddParent] = useState(null);
  const [addPartnerModalOpen, setAddPartnerModalOpen] = useState(false);
  const [editPartner, setEditPartner] = useState(null);
  const [editOfficeEntity, setEditOfficeEntity] = useState(null);
  const [addOfficeGroup, setAddOfficeGroup] = useState(null);
  const [view, setView] = useState("details");
  const [expandSignal, setExpandSignal] = useState(0);
  const [operationsOpen, setOperationsOpen] = useState(true);
  const [marketingPartnersOpen, setMarketingPartnersOpen] = useState(true);
  const [editEntity, setEditEntity] = useState(null);
  const [editParentId, setEditParentId] = useState(null);
  const [sellingYears, setSellingYears] = useState([]);
  const [selectedSellingYearId, setSelectedSellingYearId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [hasLoaded, setHasLoaded] = useState(false);

  const fetchSellingYears = useCallback(async () => {
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(getApiEndpoint("/v2/selling-year/list"), {
        method: "GET",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      });

      if (response.ok) {
        const result = await response.json();
        const years = Array.isArray(result) ? result : result.data || [];
        setSellingYears(years);
        const activeYear = years.find(y => y.is_active);
        const defaultYear = activeYear || years[0];
        if (defaultYear) {
          setSelectedSellingYearId(defaultYear.id);
        }
      }
    } catch (err) {}
  }, [getVerifiedJWT]);

  const fetchOperationEntities = useCallback(async () => {
    if (!selectedSellingYearId) return;
    setLoading(true);
    setOperations([]);
    setAvailableUsers([]);
    setEntitiesByType([]);
    setEntityList([]);
    setOperationTypes([]);
    setMarketingPartners([]);
    setOrgLevelOptions([]);
    setBaseOrgLevelName("");
    setBaseOperationLevelName("");
    try {
      const jwt = await getVerifiedJWT();
      if (!jwt) return;

      const response = await fetch(
        getApiEndpoint(`/v1/operations/entities/${selectedSellingYearId}`),
        {
          method: "GET",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${jwt}`,
          },
        }
      );

      if (response.ok) {
        const result = await response.json();
        const data = result.data || result || {};
        setOperations(Array.isArray(data.operations) ? data.operations : []);
        setAvailableUsers(
          Array.isArray(data.available_users) ? data.available_users : []
        );
        setEntitiesByType(
          Array.isArray(data.entities_by_type) ? data.entities_by_type : []
        );
        setEntityList(Array.isArray(data.entity_list) ? data.entity_list : []);
        setOperationTypes(
          Array.isArray(data.operation_types) ? data.operation_types : []
        );
        setMarketingPartners(
          Array.isArray(data.marketing_partner_groups)
            ? data.marketing_partner_groups
            : []
        );
        setOrgLevelOptions(
          Array.isArray(data.org_level_options) ? data.org_level_options : []
        );
        setBaseOrgLevelName(data.base_org_level_name || "");
        setBaseOperationLevelName(data.base_operation_level_name || "");
      }
    } catch (err) {
    } finally {
      setLoading(false);
      setHasLoaded(true);
    }
  }, [getVerifiedJWT, selectedSellingYearId]);

  const handleAddEntity = async ({ name, operationTypeId, ...connection }) => {
    const jwt = await getVerifiedJWT();
    if (!jwt) {
      throw new Error("Authentication failed");
    }

    const response = await fetch(getApiEndpoint("/v1/operations/entity"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({
        name,
        operation_type_id: operationTypeId,
        operation_parent_id: addParent?.id ?? null,
        selling_year_id: selectedSellingYearId,
        ...connection,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to create operation");
    }

    await fetchOperationEntities();
  };

  const handleOpenAddForEntity = entity => {
    setAddParent(entity);
    setAddModalOpen(true);
  };

  const handleEditEntity = async ({
    name,
    operationParentId,
    operationTypeId,
    ...connection
  }) => {
    const jwt = await getVerifiedJWT();
    if (!jwt) {
      throw new Error("Authentication failed");
    }

    const response = await fetch(getApiEndpoint("/v1/operations/entity"), {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({
        id: editEntity.id,
        name,
        operation_parent_id: operationParentId,
        selling_year_id: selectedSellingYearId,
        ...(operationTypeId != null && { operation_type_id: operationTypeId }),
        ...connection,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to update operation");
    }

    await fetchOperationEntities();
  };

  const handleDeactivateEntity = async () => {
    const jwt = await getVerifiedJWT();
    if (!jwt) {
      throw new Error("Authentication failed");
    }

    const response = await fetch(
      getApiEndpoint(
        `/v1/operations/entity/deactivate/${editEntity.id}/${selectedSellingYearId}`
      ),
      {
        method: "DELETE",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to deactivate operation");
    }

    await fetchOperationEntities();
  };

  const handleAddMarketingPartner = async ({ name }) => {
    const jwt = await getVerifiedJWT();
    if (!jwt) {
      throw new Error("Authentication failed");
    }

    const response = await fetch(getApiEndpoint("/v1/marketing-partner"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({
        name,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || "Failed to create marketing partner"
      );
    }

    await fetchOperationEntities();
  };

  const handleEditMarketingPartner = async partner => {
    const jwt = await getVerifiedJWT();
    if (!jwt) {
      throw new Error("Authentication failed");
    }

    const id = editPartner?.marketing_partner_id ?? editPartner?.id;
    const response = await fetch(
      getApiEndpoint(`/v1/marketing-partner/${id}`),
      {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${jwt}`,
        },
        body: JSON.stringify({
          ...partner,
        }),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || "Failed to update marketing partner"
      );
    }

    await fetchOperationEntities();
  };

  const handleEditOfficeEntity = async office => {
    const jwt = await getVerifiedJWT();
    if (!jwt) {
      throw new Error("Authentication failed");
    }

    const response = await fetch(getApiEndpoint("/v1/operations/office"), {
      method: "PUT",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({
        id: editOfficeEntity?.id,
        ...office,
        selling_year_id: selectedSellingYearId,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to update office");
    }

    await fetchOperationEntities();
  };

  const handleAddOffice = async office => {
    const jwt = await getVerifiedJWT();
    if (!jwt) {
      throw new Error("Authentication failed");
    }

    const response = await fetch(getApiEndpoint("/v1/operations/office"), {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${jwt}`,
      },
      body: JSON.stringify({
        ...office,
        marketing_partner_id: addOfficeGroup?.marketing_partner_id,
        selling_year_id: selectedSellingYearId,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.message || "Failed to create office");
    }

    await fetchOperationEntities();
  };

  useEffect(() => {
    fetchSellingYears();
  }, [fetchSellingYears]);

  useEffect(() => {
    fetchOperationEntities();
  }, [fetchOperationEntities]);

  const selectedSellingYear = sellingYears.find(
    y => y.id === selectedSellingYearId
  );
  const isYearLocked = selectedSellingYear?.is_locked === true;

  return (
    <div className="org-page-tab-content">
      <Group justify="space-between" align="center" mb="md">
        <SegmentedControl
          value={view}
          onChange={setView}
          data={[
            { label: "Details", value: "details" },
            { label: "Hierarchy", value: "hierarchy" },
          ]}
        />
        <Group gap="xs" align="center">
          <Text size="sm" fw={500}>
            Selling Year
          </Text>
          <Select
            value={selectedSellingYearId ? String(selectedSellingYearId) : null}
            onChange={value => {
              if (!value) return;
              const match = sellingYears.find(y => String(y.id) === value);
              setSelectedSellingYearId(match ? match.id : null);
            }}
            data={sellingYears.map(y => ({
              value: String(y.id),
              label: y.name,
            }))}
            placeholder="Select selling year"
            allowDeselect={false}
            searchable={false}
            w={200}
          />
        </Group>
      </Group>

      {loading ? (
        <Center py="xl">
          <Loader />
        </Center>
      ) : (
        <>
          {view === "details" && (
            <>
              <Group justify="space-between" align="center" mb="xs">
                <UnstyledButton onClick={() => setOperationsOpen(o => !o)}>
                  <Group gap="xs" align="center">
                    {operationsOpen ? (
                      <IconChevronDown size={20} />
                    ) : (
                      <IconChevronRight size={20} />
                    )}
                    <h2 style={{ margin: 0 }}>Operations</h2>
                  </Group>
                </UnstyledButton>
                {operationsOpen && operations.length > 0 && (
                  <Button
                    variant="subtle"
                    size="sm"
                    onClick={() => setExpandSignal(s => s + 1)}
                  >
                    Expand all
                  </Button>
                )}
              </Group>

              <Collapse in={operationsOpen}>
                <SimpleGrid cols={{ base: 2, sm: 3, md: 4 }} mb="md">
                  {entitiesByType.map(t => (
                    <Paper
                      key={t.operation_type_id ?? t.org_level_type_id}
                      p="sm"
                      withBorder
                      radius="md"
                    >
                      <Text size="xs" c="dimmed">
                        {t.operation_type_name ?? t.org_level_type_name}
                      </Text>
                      <Text size="xl" fw={700}>
                        {Array.isArray(t.entities) ? t.entities.length : 0}
                      </Text>
                    </Paper>
                  ))}
                  <Paper p="sm" withBorder radius="md">
                    <Text size="xs" c="dimmed">
                      Unassigned Users
                    </Text>
                    <Text size="xl" fw={700}>
                      {availableUsers.length}
                    </Text>
                  </Paper>
                </SimpleGrid>

                <Stack gap="sm">
                  {operations.length === 0 ? (
                    hasLoaded ? (
                      <Text c="dimmed">
                        We couldn't find any company data. Please contact
                        customer support.
                      </Text>
                    ) : null
                  ) : (
                    operations.map(entity => (
                      <OperationLevelItem
                        key={entity.id}
                        entity={entity}
                        onAdd={handleOpenAddForEntity}
                        onEdit={(ent, parentId) => {
                          setEditEntity(ent);
                          setEditParentId(parentId);
                        }}
                        onEditPartner={setEditOfficeEntity}
                        expandSignal={expandSignal}
                        allOrgLevels={orgLevelOptions}
                        baseOrgLevelName={baseOrgLevelName}
                        sellingYearId={selectedSellingYearId}
                        onTeamsUpdated={fetchOperationEntities}
                        locked={isYearLocked}
                      />
                    ))
                  )}
                </Stack>
              </Collapse>

              <Group justify="space-between" align="center" mb="xs" mt="xl">
                <UnstyledButton
                  onClick={() => setMarketingPartnersOpen(o => !o)}
                >
                  <Group gap="xs" align="center">
                    {marketingPartnersOpen ? (
                      <IconChevronDown size={20} />
                    ) : (
                      <IconChevronRight size={20} />
                    )}
                    <h2 style={{ margin: 0 }}>Marketing Partners</h2>
                  </Group>
                </UnstyledButton>
                {!isYearLocked && (
                  <Button
                    variant="subtle"
                    size="sm"
                    leftSection={<IconPlus size={16} />}
                    onClick={() => setAddPartnerModalOpen(true)}
                  >
                    Add Marketing Partner
                  </Button>
                )}
              </Group>

              <Collapse in={marketingPartnersOpen}>
                {marketingPartners.length === 0 ? (
                  hasLoaded ? (
                    <Text c="dimmed">No marketing partners yet.</Text>
                  ) : null
                ) : (
                  <Stack gap="md">
                    {marketingPartners.map(group => {
                      const groupEntities = Array.isArray(group.entities)
                        ? group.entities
                        : [];
                      return (
                        <Box key={group.marketing_partner_id}>
                          <Group justify="space-between" align="center" mb="xs">
                            {isYearLocked ? (
                              <Text fw={700}>
                                {group.marketing_partner_name}
                              </Text>
                            ) : (
                              <UnstyledButton
                                onClick={() => setEditPartner(group)}
                              >
                                <Text fw={700}>
                                  {group.marketing_partner_name}
                                </Text>
                              </UnstyledButton>
                            )}
                            {!isYearLocked && (
                              <Button
                                variant="subtle"
                                size="xs"
                                leftSection={<IconPlus size={14} />}
                                onClick={() => setAddOfficeGroup(group)}
                              >
                                Add {baseOperationLevelName || "Office"}
                              </Button>
                            )}
                          </Group>
                          {groupEntities.length === 0 ? (
                            <Text size="sm" c="dimmed">
                              No locations
                            </Text>
                          ) : (
                            <Stack gap="sm">
                              {groupEntities.map(entity => (
                                <OperationLevelItem
                                  key={entity.id}
                                  entity={entity}
                                  onAdd={handleOpenAddForEntity}
                                  onEdit={(ent, parentId) => {
                                    setEditEntity(ent);
                                    setEditParentId(parentId);
                                  }}
                                  onEditPartner={setEditOfficeEntity}
                                  expandSignal={expandSignal}
                                  allOrgLevels={orgLevelOptions}
                                  baseOrgLevelName={baseOrgLevelName}
                                  sellingYearId={selectedSellingYearId}
                                  onTeamsUpdated={fetchOperationEntities}
                                  locked={isYearLocked}
                                />
                              ))}
                            </Stack>
                          )}
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </Collapse>
            </>
          )}

          {view === "hierarchy" && (
            <div className="org-chart">
              {operations.length === 0 ? (
                hasLoaded ? (
                  <Text c="dimmed">
                    We couldn't find any company data. Please contact customer
                    support.
                  </Text>
                ) : null
              ) : (
                <ul>
                  {operations.map(entity => (
                    <OperationChartNode key={entity.id} entity={entity} />
                  ))}
                </ul>
              )}
            </div>
          )}
        </>
      )}

      <AddOperatingLevelEntityModal
        opened={addModalOpen}
        onClose={() => setAddModalOpen(false)}
        onSave={handleAddEntity}
        operationTypes={
          Array.isArray(addParent?.child_operation_types)
            ? addParent.child_operation_types
            : []
        }
      />

      <AddMarketingPartnerModal
        opened={addPartnerModalOpen}
        onClose={() => setAddPartnerModalOpen(false)}
        onSave={handleAddMarketingPartner}
      />

      <AddOfficeModal
        opened={!!addOfficeGroup}
        onClose={() => setAddOfficeGroup(null)}
        onSave={handleAddOffice}
        marketingPartnerName={addOfficeGroup?.marketing_partner_name}
        entityLabel={baseOperationLevelName || "Office"}
      />

      <EditMarketingPartnerModal
        opened={!!editPartner}
        onClose={() => setEditPartner(null)}
        marketingPartner={editPartner}
        onSave={handleEditMarketingPartner}
      />

      <EditOfficeEntityModal
        opened={!!editOfficeEntity}
        onClose={() => setEditOfficeEntity(null)}
        entity={editOfficeEntity}
        onSave={handleEditOfficeEntity}
        entityLabel={baseOperationLevelName || "Office"}
      />

      <EditOperatingLevelEntityModal
        opened={!!editEntity}
        onClose={() => {
          setEditEntity(null);
          setEditParentId(null);
        }}
        onSave={handleEditEntity}
        onDeactivate={handleDeactivateEntity}
        entity={editEntity}
        currentParentId={editParentId}
        entityList={entityList}
        operationTypes={operationTypes}
      />
    </div>
  );
}

export default OperatingLevelsTab;
